<template>
  <!-- CREATE / EDIT MODAL -->
  <div
    class="modal fade dq-modal"
    id="taxModal"
    tabindex="-1"
    ref="modalEl"
    aria-hidden="true"
    :aria-labelledby="isEdit ? 'dqTaxEditTitle' : 'dqTaxAddTitle'"
  >
    <div class="modal-dialog modal-dialog-centered modal-md">
      <div class="modal-content dq-modal-card">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="d-flex align-items-center gap-3">
            <span class="dq-icon-badge" :class="isEdit ? 'dq-icon-badge-warn' : ''">
              <i :class="isEdit ? 'ti ti-edit' : 'ti ti-receipt-tax'"></i>
            </span>

            <div class="lh-1">
              <h4
                class="modal-title mb-1"
                :id="isEdit ? 'dqTaxEditTitle' : 'dqTaxAddTitle'"
              >
                {{ isEdit ? "Edit Tax" : "Add Tax" }}
              </h4>
              <div class="dq-subtitle">
                Define a tax rate once and reuse it in invoices, budgets, and reports.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn dq-close-btn"
            data-bs-dismiss="modal"
            aria-label="Close"
            :disabled="saving"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitForm" novalidate>
          <div class="modal-body dq-modal-body">
            <div class="dq-form-grid">
              <!-- Tax Name -->
              <div class="dq-field">
                <label class="form-label dq-label">
                  Tax Name <span class="text-danger">*</span>
                </label>

                <div class="dq-input-wrap">
                  <span class="dq-input-icon">
                    <i class="ti ti-id"></i>
                  </span>
                  <input
                    ref="nameInputEl"
                    type="text"
                    v-model.trim="form.name"
                    class="form-control dq-input"
                    placeholder="e.g. VAT, Withholding Tax, Service Tax"
                    :aria-invalid="attempted && !!errors.name"
                    autocomplete="off"
                  />
                </div>

                <div v-if="attempted && errors.name" class="dq-error">
                  <i class="ti ti-alert-circle me-1"></i>{{ errors.name }}
                </div>
                <div v-else class="dq-help">
                  Use a recognizable label that will appear in dropdown selections.
                </div>
              </div>

              <!-- Rate -->
              <div class="dq-field">
                <label class="form-label dq-label">
                  Tax Percentage (%) <span class="text-danger">*</span>
                </label>

                <div class="dq-input-wrap">
                  <span class="dq-input-icon">
                    <i class="ti ti-percentage"></i>
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    v-model.number="form.rate"
                    class="form-control dq-input dq-input-right"
                    placeholder="e.g. 16"
                    :aria-invalid="attempted && !!errors.rate"
                    @input="normalizeRate"
                  />
                </div>

                <div v-if="attempted && errors.rate" class="dq-error">
                  <i class="ti ti-alert-circle me-1"></i>{{ errors.rate }}
                </div>
                <div v-else class="dq-help">
                  Enter a percentage from 0 to 100. Example: VAT is often 16%.
                </div>
              </div>

              <!-- Preview strip -->
              <div class="dq-info-strip">
                <i class="ti ti-info-circle me-2"></i>
                Preview:
                <strong class="ms-1">{{ previewLabel }}</strong>
              </div>

              <!-- Description -->
              <div class="dq-field">
                <label class="form-label dq-label">Description</label>
                <textarea
                  v-model="form.description"
                  class="form-control dq-textarea"
                  rows="3"
                  placeholder="Optional note about when this tax applies…"
                ></textarea>
                <div class="dq-help">
                  Optional. Helps admins understand what this tax is for.
                </div>
              </div>

              <!-- Status -->
              <div class="dq-field">
                <label class="form-label dq-label">Status</label>

                <div class="dq-select-wrap">
                  <span class="dq-input-icon">
                    <i class="ti ti-toggle-left"></i>
                  </span>
                  <select v-model="form.status" class="form-select dq-select">
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                <div class="dq-help">
                  <span class="dq-pill dq-pill-success" v-if="form.status === 'Active'">
                    Active
                  </span>
                  <span class="dq-pill dq-pill-muted" v-else>Inactive</span>
                  <span class="ms-2 text-muted">
                    Inactive taxes won’t appear in new selections.
                  </span>
                </div>
              </div>

              <!-- Tip card -->
              <div class="dq-tip">
                <div class="dq-tip-icon">
                  <i class="ti ti-bulb"></i>
                </div>
                <div>
                  <div class="dq-tip-title">Quick tip</div>
                  <div class="dq-tip-text">
                    If you change a tax rate, historical records may still show the old value depending on your backend rules.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sticky footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn-soft me-2"
              data-bs-dismiss="modal"
              :disabled="saving"
            >
              Cancel
            </button>

            <button type="submit" class="btn btn-primary dq-btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              <span v-if="saving">{{ isEdit ? "Saving…" : "Adding…" }}</span>
              <span v-else>{{ isEdit ? "Save Changes" : "Add Tax" }}</span>
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- DELETE MODAL -->
  <div
    class="modal fade dq-modal"
    id="deleteTaxModal"
    tabindex="-1"
    ref="deleteModalEl"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-card">
        <div class="modal-body dq-delete-body">
          <div class="dq-danger-hero">
            <span class="dq-danger-badge">
              <i class="ti ti-trash-x"></i>
            </span>
          </div>

          <h4 class="mb-1 dq-delete-title">Confirm delete</h4>
          <p class="mb-3 dq-delete-subtitle">
            You’re about to delete <strong>“{{ form.name }}”</strong>. This can’t be undone.
          </p>

          <div class="dq-delete-warning">
            <i class="ti ti-alert-triangle me-2"></i>
            Tip: Consider setting the tax to <strong>Inactive</strong> instead if it’s referenced in reports.
          </div>

          <div class="d-flex justify-content-center gap-2 mt-4">
            <button
              class="btn btn-light dq-btn-soft"
              data-bs-dismiss="modal"
              :disabled="savingDelete"
            >
              Cancel
            </button>

            <button
              class="btn btn-danger dq-btn-danger"
              @click="confirmDelete"
              :disabled="savingDelete"
            >
              <span v-if="savingDelete" class="spinner-border spinner-border-sm me-2"></span>
              <span v-if="savingDelete">Deleting…</span>
              <span v-else>Yes, Delete</span>
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from "vue";
import { Modal } from "bootstrap";
import { useTaxStore } from "@/stores/taxStore";
import { toast } from "@/utils/toast";

const emit = defineEmits(["refresh"]);
const store = useTaxStore();

const modalEl = ref(null);
const deleteModalEl = ref(null);

let modalInstance = null;
let deleteInstance = null;

const isEdit = ref(false);
const saving = ref(false);
const savingDelete = ref(false);

/* focus + validation */
const nameInputEl = ref(null);
const attempted = ref(false);

const form = ref({
  id: null,
  name: "",
  rate: "",
  description: "",
  status: "Active",
});

const errors = computed(() => {
  const e = {};
  if (!form.value.name || !String(form.value.name).trim()) e.name = "Tax name is required.";
  const r = Number(form.value.rate);
  if (form.value.rate === "" || form.value.rate === null || Number.isNaN(r)) e.rate = "Tax rate is required.";
  else if (r < 0) e.rate = "Rate cannot be negative.";
  else if (r > 100) e.rate = "Rate cannot exceed 100%.";
  return e;
});

const previewLabel = computed(() => {
  const nm = (form.value.name || "").trim() || "Tax";
  const r = Number(form.value.rate || 0);
  const pretty = Number.isFinite(r) ? r.toFixed(2).replace(/\.00$/, "") : "0";
  return `${nm} (${pretty}%)`;
});

function normalizeRate() {
  // keep it numeric but safe
  const r = Number(form.value.rate);
  if (Number.isNaN(r)) return;
  if (r < 0) form.value.rate = 0;
  if (r > 100) form.value.rate = 100;
}

function resetForm() {
  attempted.value = false;
  form.value = {
    id: null,
    name: "",
    rate: "",
    description: "",
    status: "Active",
  };
}

/* -------------------- OPEN CREATE -------------------- */
function openCreate() {
  isEdit.value = false;
  resetForm();
  showModal();
}

/* -------------------- OPEN EDIT -------------------- */
function openEdit(row) {
  isEdit.value = true;
  attempted.value = false;

  form.value = {
    id: row.id,
    name: row.name,
    rate: row.rate,
    description: row.description,
    status: row.status,
  };

  showModal();
}

/* -------------------- OPEN DELETE -------------------- */
function openDelete(row) {
  form.value = {
    ...form.value,
    id: row.id,
    name: row.name,
  };

  showDeleteModal();
}

/* -------------------- SAVE CREATE / EDIT -------------------- */
async function submitForm() {
  attempted.value = true;

  if (Object.keys(errors.value).length) {
    toast(Object.values(errors.value)[0], "error");
    nextTick(() => nameInputEl.value?.focus?.());
    return;
  }

  try {
    saving.value = true;

    // normalize payload
    const payload = {
      ...form.value,
      name: String(form.value.name).trim(),
      rate: Number(form.value.rate),
      description: form.value.description ? String(form.value.description).trim() : "",
      status: form.value.status || "Active",
    };

    if (isEdit.value) {
      await store.update(payload.id, payload);
      toast("Tax updated", "success");
    } else {
      await store.create(payload);
      toast("Tax created", "success");
    }

    hideModal();
    emit("refresh");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to save tax", "error");
  } finally {
    saving.value = false;
  }
}

/* -------------------- CONFIRM DELETE -------------------- */
async function confirmDelete() {
  try {
    savingDelete.value = true;
    await store.remove(form.value.id);

    toast("Tax deleted", "success");

    hideDeleteModal();
    emit("refresh");
  } catch (e) {
    toast(e?.response?.data?.message || "Delete failed", "error");
  } finally {
    savingDelete.value = false;
  }
}

/* -------------------- MODAL HELPERS -------------------- */
function showModal() {
  if (!modalInstance) {
    modalInstance = new Modal(modalEl.value, { backdrop: "static", keyboard: true, focus: true });
  }
  modalInstance.show();
}
function hideModal() {
  modalInstance?.hide();
}
function showDeleteModal() {
  if (!deleteInstance) {
    deleteInstance = new Modal(deleteModalEl.value, { backdrop: "static", keyboard: true, focus: true });
  }
  deleteInstance.show();
}
function hideDeleteModal() {
  deleteInstance?.hide();
}

/* Focus on open + cleanup */
function onShown() {
  nextTick(() => nameInputEl.value?.focus?.());
}
function onHidden() {
  resetForm();
}

onMounted(() => {
  if (modalEl.value) {
    modalEl.value.addEventListener("shown.bs.modal", onShown);
    modalEl.value.addEventListener("hidden.bs.modal", onHidden);
  }
});
onBeforeUnmount(() => {
  modalEl.value?.removeEventListener("shown.bs.modal", onShown);
  modalEl.value?.removeEventListener("hidden.bs.modal", onHidden);
});

defineExpose({ openCreate, openEdit, openDelete });
</script>

<style scoped>
/* ===== Premium dq modal system (same as your other upgraded modals) ===== */
.dq-modal-card {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 22px 60px rgba(16, 24, 40, 0.18);
}

.dq-modal-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 18px 18px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 1), rgba(255, 255, 255, 1));
}

.dq-subtitle {
  font-size: 0.85rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}

.dq-close-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.dq-close-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(16, 24, 40, 0.08);
}

.dq-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
  font-size: 20px;
}
.dq-icon-badge-warn {
  background: rgba(245, 158, 11, 0.14);
  color: rgba(217, 119, 6, 1);
}

.dq-modal-body {
  padding: 18px;
  max-height: 70vh;
  overflow: auto;
}

.dq-form-grid {
  display: grid;
  gap: 14px;
}

.dq-label {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 6px;
}

.dq-input-wrap,
.dq-select-wrap {
  position: relative;
}

.dq-input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(17, 24, 39, 0.45);
  z-index: 3;
  pointer-events: none;
}

.dq-input {
  padding-left: 40px !important;
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-input-right {
  text-align: right;
}
.dq-input:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12) !important;
}

.dq-select {
  padding-left: 40px !important;
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
}
.dq-select:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.10) !important;
}

.dq-textarea {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-textarea:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.10) !important;
}

.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 6px;
}

.dq-error {
  margin-top: 6px;
  font-size: 0.82rem;
  font-weight: 800;
  color: rgba(220, 38, 38, 1);
  display: inline-flex;
  align-items: center;
}

.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 0.75rem;
}
.dq-pill-success {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}
.dq-pill-muted {
  background: rgba(148, 163, 184, 0.18);
  color: rgba(71, 85, 105, 1);
}

.dq-info-strip {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(2, 132, 199, 0.08);
  border: 1px solid rgba(2, 132, 199, 0.12);
  color: rgba(17, 24, 39, 0.65);
  font-size: 0.82rem;
  font-weight: 700;
}

.dq-tip {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.12);
}
.dq-tip-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.14);
  color: rgba(79, 70, 229, 1);
}
.dq-tip-title {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 2px;
}
.dq-tip-text {
  font-size: 0.83rem;
  color: rgba(17, 24, 39, 0.6);
}

/* Sticky footer */
.dq-modal-footer {
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding: 14px 18px;
}
.dq-btn-soft {
  border-radius: 12px;
  padding: 10px 14px;
}
.dq-btn-primary {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(37, 99, 235, 0.18);
}

/* Delete modal */
.dq-delete-body {
  padding: 18px;
  text-align: center;
}
.dq-danger-hero {
  display: flex;
  justify-content: center;
  margin-top: 6px;
}
.dq-danger-badge {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.12);
  color: rgba(220, 38, 38, 1);
  font-size: 26px;
}
.dq-delete-title {
  font-weight: 950;
  letter-spacing: -0.02em;
}
.dq-delete-subtitle {
  color: rgba(17, 24, 39, 0.65);
  font-size: 0.92rem;
  line-height: 1.35rem;
}
.dq-delete-warning {
  text-align: left;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(245, 158, 11, 0.10);
  border: 1px solid rgba(245, 158, 11, 0.14);
  color: rgba(120, 53, 15, 0.95);
  font-size: 0.84rem;
}
.dq-btn-danger {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(220, 38, 38, 0.14);
}
</style>
